import {
  ACCESS_TOKEN_STORAGE_KEY,
  CURRENT_PROJECT_UUID_STORAGE_KEY,
  SAML_ORGANIZATION_UUID_STORAGE_KEY,
} from "@frontend-shared/constants";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// NOTE: NSAI-6519 — moved to frontend-shared so the mobile attachment card
// names a file type exactly as web does; re-exported for existing callers.
export {
  getFriendlyFileType,
  type TFriendlyFileTypeKey,
} from "@frontend-shared/utils/format-file-type";

export interface INumberOnlyChangeHandlerConfig {
  min?: number;
  max?: number;
  allowNegative?: boolean;
}

export const isStringNumberValid = (
  value: string,
  config?: INumberOnlyChangeHandlerConfig,
) => {
  if (value === "") {
    return true;
  }

  if (value === "-" && config?.allowNegative) {
    return true;
  }

  const convertedValue = Number(value);

  if (Number.isNaN(convertedValue)) {
    return false;
  }

  if (config?.min && convertedValue < config.min) {
    return false;
  }

  if (config?.max && convertedValue > config.max) {
    return false;
  }

  if (!config?.allowNegative && convertedValue < 0) {
    return false;
  }

  return true;
};

export const encryptEmail = (email: string): string => {
  const [local, domain] = email.split("@");
  const numAsterisks = local.length > 2 ? Math.floor(local.length / 2) : 1;
  const encryptedLocal =
    local.slice(0, local.length - numAsterisks) + "*".repeat(numAsterisks);
  return `${encryptedLocal}@${domain}`;
};

export const clearLocalStorage = () => {
  localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
  localStorage.removeItem(CURRENT_PROJECT_UUID_STORAGE_KEY);
  // NOTE: NSAI-7394 — this tab's own project pointer goes with the session, so
  // the next sign-in in this tab cannot reuse the previous user's project.
  sessionStorage.removeItem(CURRENT_PROJECT_UUID_STORAGE_KEY);
  localStorage.removeItem(SAML_ORGANIZATION_UUID_STORAGE_KEY);
};

export const getTestIdWithPrefix = (id: string, prefix?: string | undefined) =>
  prefix ? `${prefix}-${id}` : id;

export const paginateData = <TEntity>(
  data: TEntity[],
  currentPage: number,
  rowsPerPage: number,
) => {
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  return data.slice(startIndex, endIndex);
};

export const formatNumberToK = (value: number): string => {
  if (value < 1000) {
    return Math.floor(value).toString();
  }
  const flooredK = Math.floor(value / 1000);
  return `${flooredK}k`;
};

export const formatError = (rawError: unknown): string => {
  if (typeof rawError === "string") {
    return rawError;
  }

  if (
    rawError &&
    typeof rawError === "object" &&
    "message" in rawError &&
    typeof (rawError as { message?: unknown }).message === "string"
  ) {
    return (rawError as { message: string }).message;
  }

  try {
    return JSON.stringify(rawError);
  } catch {
    return "Unknown error";
  }
};
